import {
  CartLinesUpdateEvent,
  CartErrorEvent,
  StandardEvents,
} from '@shopify/events';

const IMPRINT_PROPERTY = '_requires_imprint_kit';

/*
 * Variant ID ufficiale del Kit Impronta.
 *
 * Serve quando il cliente rimuove il Kit dal carrello:
 * non avendo più la riga del Kit da cui leggere il variant_id,
 * dobbiamo sapere quale variante riaggiungere.
 */
const IMPRINT_KIT_VARIANT_ID = 54885898748231;


/* =========================================================
   LETTURA CARRELLO
   ========================================================= */

async function getCurrentCart() {
  const cartItemsComponent = document.querySelector(
    'cart-items-component'
  );

  if (
    cartItemsComponent &&
    typeof cartItemsComponent.fetchCartData === 'function'
  ) {
    return cartItemsComponent.fetchCartData();
  }

  const response = await fetch(
    `${Theme.routes.cart_url}.json`,
    {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    }
  );

  if (!response.ok) {
    throw new Error('Impossibile leggere il carrello');
  }

  return response.json();
}


/* =========================================================
   SEZIONI CARRELLO HORIZON
   ========================================================= */

function getCartSectionIds() {
  const sectionIds = new Set();

  document
    .querySelectorAll('cart-items-component')
    .forEach((component) => {
      if (
        component instanceof HTMLElement &&
        component.dataset.sectionId
      ) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

  return Array.from(sectionIds);
}


/* =========================================================
   PROPRIETÀ PRODOTTO
   ========================================================= */

function getProductProperties(formData) {
  const properties = {};

  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);

    if (
      match &&
      typeof value === 'string' &&
      value !== ''
    ) {
      properties[match[1]] = value;
    }
  }

  return properties;
}


/* =========================================================
   RICONOSCIMENTO KIT IMPRONTA
   ========================================================= */

/*
 * Il Kit viene riconosciuto in due modi:
 *
 * 1. Variant ID ufficiale.
 * 2. Proprietà invisibile _imprint_kit.
 *
 * Il doppio controllo rende il sistema più robusto
 * anche con carrelli creati prima di questa modifica.
 */
function isImprintKitItem(item) {
  if (!item) return false;

  if (
    String(item.variant_id) ===
    String(IMPRINT_KIT_VARIANT_ID)
  ) {
    return true;
  }

  return (
    item.properties &&
    String(item.properties._imprint_kit) === 'true'
  );
}


/* =========================================================
   AGGIUNTA PRODOTTO IPSE + KIT
   ========================================================= */

document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const productForm = form.closest(
      'product-form-component'
    );

    if (!(productForm instanceof HTMLElement)) return;

    /*
     * Tutti i prodotti normali continuano a usare
     * Horizon senza alcuna interferenza.
     */
    if (productForm.dataset.requiresKit !== 'true') {
      return;
    }

    /*
     * Solo per i prodotti IPSE che richiedono il Kit
     * fermiamo il submit standard di Horizon.
     */
    event.preventDefault();
    event.stopImmediatePropagation();

    const kitVariantId =
      productForm.dataset.kitVariantId;

    if (!kitVariantId) {
      console.error(
        'IPSE: Variant ID del Kit Impronta mancante'
      );
      return;
    }

    const formData = new FormData(form);

    const productVariantId = formData.get('id');

    if (!productVariantId) {
      console.error(
        'IPSE: Variant ID del gioiello mancante'
      );
      return;
    }

    const quantity =
      Number(formData.get('quantity')) ||
      Number(productForm.dataset.quantityDefault) ||
      1;

    const submitButton = form.querySelector(
      '[ref="addToCartButton"]'
    );

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    /*
     * Comunichiamo subito ad Horizon che
     * è iniziata un'aggiunta al carrello.
     */
    productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: String(
              productVariantId
            ),
            quantity,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const currentCart = await getCurrentCart();

      const kitAlreadyInCart =
        currentCart.items.some(
          (item) =>
            String(item.variant_id) ===
            String(kitVariantId)
        );

      /*
       * Manteniamo tutte le eventuali proprietà
       * già presenti nel form.
       */
      const productProperties =
        getProductProperties(formData);

      /*
       * Marchiamo il gioiello come prodotto
       * che necessita del Kit.
       */
      productProperties[IMPRINT_PROPERTY] =
        'true';

      const items = [
        {
          id: Number(productVariantId),
          quantity,
          properties: productProperties,
        },
      ];

      /*
       * Il Kit viene aggiunto soltanto se
       * non è già presente nel carrello.
       *
       * Rimane sempre quantità 1.
       */
      if (!kitAlreadyInCart) {
        items.push({
          id: Number(kitVariantId),
          quantity: 1,
          properties: {
            _imprint_kit: 'true',
          },
        });
      }

      /*
       * Manteniamo eventuali selling plan.
       */
      const sellingPlan =
        formData.get('selling_plan');

      if (sellingPlan) {
        items[0].selling_plan =
          Number(sellingPlan);
      }

      const sectionIds = getCartSectionIds();

      /*
       * Gioiello e Kit entrano nella stessa
       * richiesta Shopify.
       *
       * Se il Kit è esaurito Shopify respinge
       * l'intera operazione, evitando di lasciare
       * il gioiello da solo.
       */
      const response = await fetch(
        Theme.routes.cart_add_url,
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items,
            sections:
              sectionIds.join(','),
            sections_url:
              window.location.pathname,
          }),
        }
      );

      const addResponse =
        await response.json();

      if (!response.ok || addResponse.status) {
        throw new Error(
          addResponse.description ||
            addResponse.message ||
            'Errore durante aggiunta al carrello'
        );
      }

      /*
       * Leggiamo il carrello definitivo soltanto
       * dopo l'aggiunta riuscita.
       */
      const updatedCart =
        await getCurrentCart();

      deferredEventPromise.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            updatedCart
          ),

        detail: {
          items: updatedCart.items,
          sections: addResponse.sections,
          source: 'ipse-imprint-kit',
          sourceId:
            productForm.id ||
            'ipse-product-form',

          itemCount: items.reduce(
            (total, item) =>
              total + item.quantity,
            0
          ),

          productId:
            productForm.dataset.productId,

          didError: false,
        },
      });
    } catch (error) {
      console.error(
        'IPSE Kit Impronta:',
        error
      );

      /*
       * IMPORTANTISSIMO:
       * chiudiamo correttamente anche la Promise
       * che Horizon sta aspettando.
       */
      deferredEventPromise.reject(error);

      productForm.dispatchEvent(
        new CartErrorEvent({
  error:
    error?.message ||
    window.ipseTranslations.errorAddKit,

  code: 'SERVICE_UNAVAILABLE',
})
      );
    } finally {
      if (
        submitButton instanceof HTMLButtonElement
      ) {
        submitButton.disabled = false;
      }
    }
  },

  true
);


/* =========================================================
   TOAST — KIT RIMOSSO
   ========================================================= */

function showImprintKitRemovedToast() {
  const existingToast =
    document.querySelector(
      '.ipse-imprint-kit-toast'
    );

  if (existingToast) {
    existingToast.remove();
  }

  const toast =
    document.createElement('div');

  toast.className =
    'ipse-imprint-kit-toast';

  toast.setAttribute(
    'role',
    'status'
  );

  toast.setAttribute(
    'aria-live',
    'polite'
  );

 toast.innerHTML = `
  <strong>${window.ipseTranslations.removedTitle}</strong>
  <span>${window.ipseTranslations.removedMessage}</span>
`;

  const cartDrawerDialog =
    document.querySelector(
      '#cart-drawer dialog'
    );

  if (cartDrawerDialog) {
    cartDrawerDialog.appendChild(toast);
  } else {
    document.body.appendChild(toast);
  }

  requestAnimationFrame(() => {
    toast.classList.add(
      'ipse-imprint-kit-toast--visible'
    );
  });

  setTimeout(() => {
    toast.classList.remove(
      'ipse-imprint-kit-toast--visible'
    );

    setTimeout(() => {
      toast.remove();
    }, 300);
  }, 4500);
}


/* =========================================================
   TOAST — KIT RIPRISTINATO
   ========================================================= */

function showImprintKitRestoredToast() {
  const existingToast =
    document.querySelector(
      '.ipse-imprint-kit-toast'
    );

  if (existingToast) {
    existingToast.remove();
  }

  const toast =
    document.createElement('div');

  toast.className =
    'ipse-imprint-kit-toast';

  toast.setAttribute(
    'role',
    'status'
  );

  toast.setAttribute(
    'aria-live',
    'polite'
  );

  toast.innerHTML = `
  <strong>${window.ipseTranslations.requiredTitle}</strong>
  <span>${window.ipseTranslations.requiredMessage}</span>
`;

  const cartDrawerDialog =
    document.querySelector(
      '#cart-drawer dialog'
    );

  if (cartDrawerDialog) {
    cartDrawerDialog.appendChild(toast);
  } else {
    document.body.appendChild(toast);
  }

  requestAnimationFrame(() => {
    toast.classList.add(
      'ipse-imprint-kit-toast--visible'
    );
  });

  setTimeout(() => {
    toast.classList.remove(
      'ipse-imprint-kit-toast--visible'
    );

    setTimeout(() => {
      toast.remove();
    }, 300);
  }, 4500);
}


/* =========================================================
   IPSE — SINCRONIZZAZIONE KIT
   =========================================================

   REGOLE:

   A)
   Gioiello IPSE presente
   + Kit presente
   = non fare nulla.

   B)
   Gioiello IPSE presente
   + Kit assente
   = riaggiungi Kit.

   C)
   Nessun gioiello IPSE
   + Kit presente
   = rimuovi Kit.

   D)
   Nessun gioiello IPSE
   + nessun Kit
   = non fare nulla.

   ========================================================= */

let imprintKitSyncInProgress = false;


document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    /*
     * Se siamo già dentro una sincronizzazione,
     * non iniziamone una seconda in parallelo.
     */
    if (imprintKitSyncInProgress) {
      return;
    }

    /*
     * Attendiamo che l'azione del cliente
     * sia realmente conclusa.
     */
    Promise.resolve(event.promise)
      .then(async () => {
        if (imprintKitSyncInProgress) {
          return;
        }

        imprintKitSyncInProgress = true;

        try {
          const cart =
            await getCurrentCart();

          /*
           * Cerchiamo almeno un gioiello
           * che richiede il Kit.
           */
          const hasImprintProduct =
            cart.items.some(
              (item) =>
                item.properties &&
                String(
                  item.properties[
                    IMPRINT_PROPERTY
                  ]
                ) === 'true'
            );

          /*
           * Cerchiamo il Kit.
           */
          const imprintKitItem =
            cart.items.find(
              (item) =>
                isImprintKitItem(item)
            );


          /* =================================================
             CASO B

             Gioiello presente + Kit assente
             → RIAGGIUNGI KIT
             ================================================= */

          if (
            hasImprintProduct &&
            !imprintKitItem
          ) {
            const sectionIds =
              getCartSectionIds();

            const deferredEventPromise =
              CartLinesUpdateEvent.createPromise();

            /*
             * Horizon viene informato che
             * sta iniziando un aggiornamento
             * automatico del carrello.
             */
            document.dispatchEvent(
              new CartLinesUpdateEvent({
                action: 'add',
                context: 'cart',

                lines: [
                  {
                    merchandiseId:
                      String(
                        IMPRINT_KIT_VARIANT_ID
                      ),

                    quantity: 1,
                  },
                ],

                promise:
                  deferredEventPromise.promise,
              })
            );

            try {
              const response =
                await fetch(
                  Theme.routes.cart_add_url,
                  {
                    method: 'POST',

                    headers: {
                      'Content-Type':
                        'application/json',

                      Accept:
                        'application/json',
                    },

                    credentials:
                      'same-origin',

                    body: JSON.stringify({
                      items: [
                        {
                          id:
                            IMPRINT_KIT_VARIANT_ID,

                          quantity: 1,

                          properties: {
                            _imprint_kit:
                              'true',
                          },
                        },
                      ],

                      sections:
                        sectionIds.join(','),

                      sections_url:
                        window.location.pathname,
                    }),
                  }
                );

              const addResponse =
                await response.json();

              if (
                !response.ok ||
                addResponse.status
              ) {
                throw new Error(
  addResponse.description ||
  addResponse.message ||
  window.ipseTranslations.errorRestoreKit
);
              }

              const updatedCart =
                await getCurrentCart();

              deferredEventPromise.resolve({
                cart:
                  CartLinesUpdateEvent.createCartFromAjaxResponse(
                    updatedCart
                  ),

                detail: {
                  items:
                    updatedCart.items,

                  sections:
                    addResponse.sections,

                  source:
                    'ipse-imprint-kit-sync',

                  sourceId:
                    'ipse-imprint-kit-sync',

                  itemCount: 1,

                  didError: false,
                },
              });

              showImprintKitRestoredToast();
            } catch (error) {
              console.error(
                'IPSE: impossibile ripristinare il Kit Impronta',
                error
              );

              /*
               * Chiudiamo correttamente la Promise
               * anche se Shopify rifiuta il Kit,
               * ad esempio perché nel frattempo
               * l'inventario è arrivato a zero.
               */
              deferredEventPromise.reject(
                error
              );

              document.dispatchEvent(
                new CartErrorEvent({
                  error:
  error?.message ||
  window.ipseTranslations.errorRestoreKit,

                  code:
                    'SERVICE_UNAVAILABLE',
                })
              );
            }

            /*
             * Abbiamo gestito questo stato.
             * Non serve continuare con
             * gli altri controlli.
             */
            return;
          }


          /* =================================================
             CASO C

             Nessun gioiello IPSE + Kit presente
             → RIMUOVI KIT

             Questa è la logica che avevi già.
             ================================================= */

          if (
            !hasImprintProduct &&
            imprintKitItem
          ) {
            const sectionIds =
              getCartSectionIds();

            const deferredEventPromise =
              CartLinesUpdateEvent.createPromise();

            document.dispatchEvent(
              new CartLinesUpdateEvent({
                action: 'update',
                context: 'cart',

                lines: [
                  {
                    merchandiseId:
                      String(
                        imprintKitItem.variant_id
                      ),

                    quantity: 0,
                  },
                ],

                promise:
                  deferredEventPromise.promise,
              })
            );

            try {
              const response =
                await fetch(
                  Theme.routes.cart_change_url,
                  {
                    method: 'POST',

                    headers: {
                      'Content-Type':
                        'application/json',

                      Accept:
                        'application/json',
                    },

                    credentials:
                      'same-origin',

                    body: JSON.stringify({
                      id:
                        imprintKitItem.key,

                      quantity: 0,

                      sections:
                        sectionIds.join(','),

                      sections_url:
                        window.location.pathname,
                    }),
                  }
                );

              const updatedCart =
                await response.json();

              if (
                !response.ok ||
                updatedCart.status
              ) {
                throw new Error(
  updatedCart.description ||
  updatedCart.message ||
  window.ipseTranslations.errorRemoveKit
);
              }

              deferredEventPromise.resolve({
                cart:
                  CartLinesUpdateEvent.createCartFromAjaxResponse(
                    updatedCart
                  ),

                detail: {
                  items:
                    updatedCart.items,

                  sections:
                    updatedCart.sections,

                  source:
                    'ipse-imprint-kit-sync',

                  sourceId:
                    'ipse-imprint-kit-sync',

                  itemCount: 0,

                  didError: false,
                },
              });

              showImprintKitRemovedToast();
            } catch (error) {
              console.error(
                'IPSE: impossibile rimuovere automaticamente il Kit Impronta',
                error
              );

              /*
               * Anche la vecchia sincronizzazione
               * ora chiude correttamente la Promise
               * in caso di errore.
               */
              deferredEventPromise.reject(
                error
              );

              document.dispatchEvent(
                new CartErrorEvent({
                  error:
  error?.message ||
  window.ipseTranslations.errorRemoveKit,

                  code:
                    'SERVICE_UNAVAILABLE',
                })
              );
            }
          }
        } catch (error) {
          console.error(
            'IPSE: errore sincronizzazione Kit Impronta',
            error
          );
        } finally {
          imprintKitSyncInProgress = false;
        }
      })
      .catch((error) => {
        /*
         * Se l'aggiornamento originario del carrello
         * è fallito, non tentiamo una sincronizzazione
         * su uno stato incompleto.
         */
        console.error(
          'IPSE: errore dopo aggiornamento carrello',
          error
        );
      });
  }
);